import React from 'react';
import * as styles from '../../styles/components/project-view.css.ts';
import { TabContainer } from '../../tab-system/TabContainer';
import { PaneRenderer } from '../PaneRenderer';
import { DragOverlay } from '../DragOverlay';
import { WorkspaceTabs } from '../WorkspaceTabs';
import { ReportProvider } from '../../contexts/ReportContext';
import TabCreationComboBox, { TabOption } from '../TabCreationComboBox';
import { PaneNode, DragState } from '../../../types/pane.types';
import { MIDASProject } from '../../../types/project.types';
import { DropZone } from '../../../types';
import { Report, ReportElementUnion } from '../../../types/report.types';
import { ReportActionData } from '../ReportActions';
import { MenuBar } from '../MenuBar/MenuBar';
import type { TabCreationComboBoxRef } from '../TabCreationComboBox';
import logger from '../../utils/logger';

interface ProjectViewContentProps {
  // 基本的な状態
  isLoading: boolean;
  error: string | null;
  project: MIDASProject | null;

  // レイアウト
  layout: {
    rootPane: PaneNode;
    activeContainerId: string | null | undefined;
  };

  // イベントハンドラー
  handleTabDragStart: (tabId: string, containerId: string, event: React.DragEvent) => void;
  handleTabDragEnd: () => void;
  handleTabDrop: (draggedTabId: string, targetContainerId: string, dropIndex?: number) => void;
  handleDropZone: (dropZone: DropZone, draggedTabId: string, draggedFromPaneId: string) => void;
  
  // ドラッグオーバーレイ
  isDragging: boolean;
  dragState: DragState | undefined;
  
  // タブ作成オプション
  getTabCreationOptions: () => TabOption[];
  tabCreationComboBoxRef: React.RefObject<TabCreationComboBoxRef | null>;

  // 永続化関連
  persistenceManager?: {
    saveProjectToFile: () => Promise<boolean>;
    saveProjectToFileAs: () => Promise<boolean>;
  };
  
  // レポート関連
  reportContextValue: {
    availableReports: Report[];
    onAddToReport: (reportId: string, data: ReportActionData) => Promise<void>;
    onCreateReport: (data: ReportActionData, reportName?: string) => Promise<void>;
    onAddToExistingReport: (reportId: string, data: ReportActionData) => Promise<void>;
    onCreateReportAndOpen: (data: ReportActionData, reportName?: string) => Promise<void>;
    selectedReportId: string | null;
    onSelectReport: (reportId: string | null) => void;
    onDeleteReport: (reportId: string) => Promise<void>;
    onUpdateReport: (reportId: string, updates: Partial<Report>) => Promise<void>;
    onUpdateReportElement: (reportId: string, elementId: string, updates: Partial<ReportElementUnion>) => Promise<void>;
    onExportReport: (reportId: string) => Promise<void>;
    onDuplicateReport: (reportId: string) => Promise<void>;
    onRenameReport: (reportId: string, newName: string) => Promise<void>;
  };
}

/**
 * ProjectViewのUI描画を担当するコンポーネント
 * 状態管理は親コンポーネントに委譲し、純粋な表示ロジックのみ
 */
export function ProjectViewContent({
  isLoading,
  error,
  project,
  layout,
  handleTabDragStart,
  handleTabDragEnd,
  handleTabDrop,
  handleDropZone,
  isDragging,
  dragState,
  getTabCreationOptions,
  tabCreationComboBoxRef,
  persistenceManager,
  reportContextValue
}: ProjectViewContentProps) {
  
  // ローディング状態
  if (isLoading) {
    return (
      <div className={styles.appLayout}>
        <main className={styles.mainContent}>
          <div className={styles.loadingState}>
            <h3>Loading project...</h3>
            <p>Please wait while the project is being loaded.</p>
          </div>
        </main>
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div className={styles.appLayout}>
        <main className={styles.mainContent}>
          <div className={styles.errorState}>
            <h3>❌ エラーが発生しました</h3>
            <p>{error}</p>
            <button className={`${styles.actionButton} ${styles.secondary}`} onClick={() => window.close()}>
              Close Window
            </button>
          </div>
        </main>
      </div>
    );
  }

  // プロジェクトが存在しない
  if (!project) {
    return (
      <div className={styles.appLayout}>
        <main className={styles.mainContent}>
          <div className={styles.errorState}>
            <h3>Project not found</h3>
            <p>The project could not be loaded or has been deleted.</p>
            <button className={`${styles.actionButton} ${styles.secondary}`} onClick={() => window.close()}>
              Close Window
            </button>
          </div>
        </main>
      </div>
    );
  }

  // メインコンテンツのレンダリング
  return (
    <ReportProvider value={reportContextValue}>
      <div className={styles.appLayout} data-testid="project-view">
        <main className={styles.mainContent}>
        {/* Phase 1: 全画面タブコンテナレイアウト（サイドバー廃止） */}
        <div className={styles.fullTabLayout}>
          {/* メニューバーとTabCreationComboBoxを統合 */}
          <MenuBar
            onSave={persistenceManager?.saveProjectToFile}
            onSaveAs={persistenceManager?.saveProjectToFileAs}
            onReturnToLauncher={() => {
              logger.info('[ProjectViewContent] Returning to launcher');
              // React Routerを使用してLauncherViewに遷移
              window.location.href = '/';
            }}
            onNewTab={(tabType) => {
              // タブ作成ロジックを呼び出す
              const options = getTabCreationOptions();
              const option = options.find(opt => opt.value === tabType || opt.id === tabType);
              if (option && tabCreationComboBoxRef.current) {
                tabCreationComboBoxRef.current.handleSelect(option);
              }
            }}
            tabCreationComboBox={
              <TabCreationComboBox
                ref={tabCreationComboBoxRef}
                options={getTabCreationOptions()}
                projectName={project?.projectName}
                placeholder="Press ↓ or type to call functions..."
                data-testid="tab-creation-combobox"
              />
            }
          />

          {/* Phase 3: 分割ペインシステム */}
          <div style={{ position: 'relative', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {layout.rootPane.type === 'container' ? (
              // シンプルなタブコンテナ
              <TabContainer
                container={layout.rootPane}
                isActive={true}
                onTabDragStart={handleTabDragStart}
                onTabDragEnd={handleTabDragEnd}
                onTabDrop={handleTabDrop}
              />
            ) : (
              // 分割ペインレイアウト
              <PaneRenderer
                node={layout.rootPane}
                onTabDragStart={handleTabDragStart}
                onTabDragEnd={handleTabDragEnd}
                onTabDrop={handleTabDrop}
              />
            )}
          </div>

          {/* ワークスペースタブバー */}
          <WorkspaceTabs />
        </div>

        {/* ドラッグオーバーレイ */}
        <DragOverlay
          isDragging={isDragging}
          dragState={dragState}
          onDrop={handleDropZone}
        />
        </main>
      </div>
    </ReportProvider>
  );
}